import { usersApi } from './adminApi';
import { handleApiError } from './notificationService';
import { User, UsersApiResponse } from '../types';

export interface UsersListResponse {
  data: User[];
  total: number;
  current: number;
  pageSize: number;
}

export interface UserFilters {
  phoneVerified?: string | boolean;
  isAppUser?: string | boolean;
  hasUpdatedInfo?: string | boolean;
  userSource?: string;
  search?: string;
  filter?: Record<string, any>;
  createdAtFrom?: string;
  createdAtTo?: string;
}

export interface CreateUserData {
  name: string;
  email: string | null;
  phone: string;
  isAppUser?: boolean;
  energy?: number;
}

export interface UpdateUserData {
  name?: string;
  email?: string | null;
  phone?: string;
  isAppUser?: boolean;
  energy?: number;
}

// Get users with pagination and filters
export const getUsers = async (
  page: number = 1,
  pageSize: number = 10,
  filters?: UserFilters
): Promise<UsersListResponse> => {
  try {
    // Use admin API with proper parameters - pass all filter params
    const response: UsersApiResponse = await usersApi.getAllUsers(
      page, 
      pageSize, 
      filters?.search, 
      filters?.filter,
      filters?.createdAtFrom,
      filters?.createdAtTo
    );
    
    return {
      data: response.data?.users || [],
      total: response.data?.pagination?.totalUsers || 0,
      current: response.data?.pagination?.currentPage || page,
      pageSize: response.data?.pagination?.limit || pageSize
    };
  } catch (error) {
    handleApiError(error as Error);
    // Return mock data if API fails (for development)
    return getUsersMock();
  }
};

// Get user by ID
export const getUserById = async (userId: string): Promise<any> => {
  try {
    const response = await usersApi.getUserById(userId);
    return response.data;
  } catch (error) {
    handleApiError(error as Error);
    throw error;
  }
};

// Create new user
export const createUser = async (userData: CreateUserData): Promise<any> => {
  try {
    const response = await usersApi.createUser(userData);
    return response.data;
  } catch (error) {
    handleApiError(error as Error);
    throw error;
  }
};

// Update user
export const updateUser = async (userId: string, userData: UpdateUserData): Promise<any> => {
  try {
    const response = await usersApi.updateUser(userId, userData);
    return response.data;
  } catch (error) {
    handleApiError(error as Error);
    throw error;
  }
};

// Delete user
export const deleteUser = async (userId: string): Promise<void> => {
  try {
    await usersApi.deleteUser(userId);
  } catch (error) {
    handleApiError(error as Error);
    throw error;
  }
};

// Get user statistics
export const getUserStatistics = async (): Promise<any> => {
  try {
    const response = await usersApi.getUserStatistics();
    return response.data;
  } catch (error) {
    handleApiError(error as Error);
    throw error;
  }
};

// Update user status
export const updateUserStatus = async (userId: string, isActive: boolean): Promise<any> => {
  try {
    const response = await usersApi.updateUserStatus(userId, isActive);
    return response.data;
  } catch (error) {
    handleApiError(error as Error);
    throw error;
  }
};

// Bulk update users
export const bulkUpdateUsers = async (userIds: string[], field: string, value: any): Promise<any> => {
  try {
    const response = await usersApi.bulkUpdateUsers(userIds, field, value);
    return response.data;
  } catch (error) {
    handleApiError(error as Error);
    throw error;
  }
};

// Export users
export const exportUsers = async (userIds: string[], format: string = 'csv'): Promise<any> => {
  try {
    const response = await usersApi.exportUsers(userIds, format);
    return response.data;
  } catch (error) {
    handleApiError(error as Error);
    throw error;
  }
};

// Update user energy
export const updateUserEnergy = async (userId: string, energy: number): Promise<any> => {
  try {
    const response = await usersApi.updateUserEnergy(userId, energy);
    return response.data;
  } catch (error) {
    handleApiError(error as Error);
    throw error;
  }
};

// Mock data function for development/fallback
export function getUsersMock(): UsersListResponse {
  return {
    data: [
      {
        id: 'e088d2c5-4abe-467a-96be-22183b841ab3',
        name: 'Nguyễn Văn Test',
        username: 'Nguyễn Văn Test',
        email: null,
        phone: 'webu8jre9qot',
        energy: 1,
        isAppUser: false,
        phoneVerified: true,
        province: null,
        ward: null,
        gender: null,
        birthYear: null,
        refBy: null,
        status: 'active',
        role: 'web_user',
        lastLoginAt: '2025-08-31T12:40:08.740Z',
        createdAt: '2025-08-31T05:40:08.719Z',
        updatedAt: '2025-08-31T06:03:30.741Z',
        profile: {
          firstName: 'Nguyễn Văn',
          lastName: 'Test'
        },
        stats: {
          totalGamesPlayed: 45,
          totalScore: 12350,
          achievementsCount: 8,
          streakDays: 7
        }
      },
      {
        id: 'fc060d9b-a869-491e-a50e-b3ce57a96697',
        name: 'Nguyen Van A',
        username: 'Nguyen Van A',
        email: null,
        phone: 'temp_55491638',
        energy: 1,
        isAppUser: true,
        phoneVerified: false,
        province: null,
        ward: null,
        gender: null,
        birthYear: null,
        refBy: null,
        status: 'unverified',
        role: 'app_user',
        lastLoginAt: '2025-08-29T08:33:23.120Z',
        createdAt: '2025-08-29T01:18:11.639Z',
        updatedAt: '2025-08-29T01:33:26.985Z',
        profile: {
          firstName: 'Nguyen Van',
          lastName: 'A'
        },
        stats: {
          totalGamesPlayed: 28,
          totalScore: 7950,
          achievementsCount: 5,
          streakDays: 3
        }
      }
    ] as User[],
    total: 78,
    current: 1,
    pageSize: 10
  };
}

// Alias functions for backward compatibility
export const getAllUsers = getUsers;
export const getUserStats = getUserStatistics;
export const bulkUserActions = bulkUpdateUsers;

// Additional utility functions
export const suspendUser = async (userId: string, _reason?: string): Promise<any> => {
  return updateUserStatus(userId, false);
};

export const reactivateUser = async (userId: string): Promise<any> => {
  return updateUserStatus(userId, true);
};

export const getUserActivity = async (
  _userId: string,
  page: number = 1,
  pageSize: number = 10
): Promise<any> => {
  try {
    // Note: This would need to be implemented in the admin API
    // For now, return mock data
    return {
      data: [],
      total: 0,
      current: page,
      pageSize
    };
  } catch (error) {
    handleApiError(error as Error);
    throw error;
  }
};

// Search users
export const searchUsers = async (
  query: string,
  filters?: UserFilters,
  page: number = 1,
  pageSize: number = 10
): Promise<UsersListResponse> => {
  return getUsers(page, pageSize, { ...filters, search: query });
};
